import { isNative, getPlatform } from './platform';
import { supabase } from '@/integrations/supabase/client';

// Device-session registration (Phase 1 — remote-wipe backbone, frontend half).
//
// On login the mobile app gathers a stable device identifier + device info + app version + (best-effort)
// the native push token and calls register_device_session, persisting the returned device_session id
// locally so the device can later self-identify (the AdminSecuritySheet self-revoke view) and so the
// wipe handler can clear it. The backbone RPC (register_device_session) is SECURITY DEFINER and upserts
// on (auth.uid(), device_id), so this is idempotent and safe to re-run on every login / reconnect.
//
// Web is a NO-OP (guarded by isNative()): device sessions are a native-device concern, the website build
// is unaffected. OFFLINE-RESILIENT: never throws — on failure (offline / no session / plugin missing) it
// swallows the error and leaves a "needs-retry" flag so the next login/reconnect re-attempts. Because the
// RPC upserts, a retry never creates a duplicate row.
//
// NOTE: device_sessions is a new table not yet in the generated Supabase types, and register_device_session
// is not in the generated RPC map — the `as any` casts are deliberate, temporary escape hatches. Regenerate
// src/integrations/supabase/types.ts (Supabase MCP generate_typescript_types) to remove them.

/** Preferences key holding the device_session id returned by register_device_session. */
export const DEVICE_SESSION_ID_KEY = 'cbam.device_session_id';

/** Preferences flag set when a registration attempt failed and should be retried on next login/reconnect. */
const DEVICE_SESSION_RETRY_KEY = 'cbam.device_session_retry';

/** Locally generated, persisted device id used when @capacitor/device cannot supply a stable identifier. */
const DEVICE_ID_FALLBACK_KEY = 'cbam.device_id';

/** Single-flight guard so overlapping login + reconnect triggers don't fire the RPC twice concurrently. */
let inFlight = false;

async function getStableDeviceId(): Promise<string> {
  // Preferred: @capacitor/device's per-install identifier (stable across app launches).
  try {
    const { Device } = await import('@capacitor/device');
    const { identifier } = await Device.getId();
    if (identifier) return identifier;
  } catch {
    /* plugin unavailable / web — fall through to the persisted fallback */
  }

  // Fallback: a UUID we generate once and persist in Preferences. Keeps device_id stable across launches
  // even if Device.getId() is unavailable, so the RPC upsert continues to target the same row.
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const existing = await Preferences.get({ key: DEVICE_ID_FALLBACK_KEY });
    if (existing.value) return existing.value;
    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    await Preferences.set({ key: DEVICE_ID_FALLBACK_KEY, value: generated });
    return generated;
  } catch {
    // Last resort: an ephemeral id (still lets registration proceed; worst case a new row per launch,
    // which the user can revoke). Never block the wipe backbone on a missing device id.
    return `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

async function getDeviceInfo(): Promise<{ model: string; osVersion: string }> {
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return { model: info.model ?? '', osVersion: info.osVersion ?? '' };
  } catch {
    return { model: '', osVersion: '' };
  }
}

async function getAppVersion(): Promise<string> {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    // Marketing version + build, mirroring versionGate's use of App.getInfo().
    return info.build ? `${info.version} (${info.build})` : info.version;
  } catch {
    return '';
  }
}

/**
 * Best-effort native push token for this device. push.ts upserts the FCM/APNs token into
 * public.push_subscriptions.native_token on the 'registration' event but does not export it, so we read
 * the most-recent row for this user+platform. Returns null when unavailable — register_device_session
 * accepts a null token and the push registration path remains the source of truth for the token.
 */
async function getPushTokenBestEffort(): Promise<string | null> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return null;

    const { data } = await (supabase.from('push_subscriptions' as never) as any)
      .select('native_token')
      .eq('user_id', userId)
      .eq('platform', getPlatform())
      .not('native_token', 'is', null)
      // Order by last_used_at — a column research-notify is confirmed to write on each delivery.
      // (The previous 'updated_at' may not exist on push_subscriptions; an unknown column errors the
      //  query, the catch swallows it, and registration falls back to a null token — so the token was
      //  effectively always null via this best-effort path.)
      .order('last_used_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    const token = (data as { native_token?: string | null } | null)?.native_token;
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

async function setRetryFlag(value: boolean): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    if (value) {
      await Preferences.set({ key: DEVICE_SESSION_RETRY_KEY, value: '1' });
    } else {
      await Preferences.remove({ key: DEVICE_SESSION_RETRY_KEY });
    }
  } catch {
    /* preferences unavailable — ignore; the next login still re-runs registration */
  }
}

/**
 * Register (or refresh) this device's session row and persist the returned id.
 *
 * Native-only, fire-and-forget, NEVER throws. Idempotent: register_device_session upserts on
 * (auth.uid(), device_id), so repeated calls (every login, plus reconnect) converge on one row and keep
 * last_seen_at / app_version / push_token fresh. On failure it sets a retry flag so the next
 * login/reconnect re-attempts; on success it clears that flag.
 */
export async function registerDeviceSession(): Promise<void> {
  if (!isNative()) return;
  if (inFlight) return;
  inFlight = true;

  try {
    // Require an authenticated session — the RPC raises "not authenticated" otherwise. Flag for retry so
    // a call that races ahead of session restore re-attempts on the next trigger.
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user?.id) {
      await setRetryFlag(true);
      return;
    }

    const deviceId = await getStableDeviceId();
    const { model, osVersion } = await getDeviceInfo();
    const appVersion = await getAppVersion();
    const pushToken = await getPushTokenBestEffort();

    const { data, error } = await (supabase.rpc as any)('register_device_session', {
      _device_id: deviceId,
      _platform: getPlatform(),
      _model: model,
      _os_version: osVersion,
      _app_version: appVersion,
      _push_token: pushToken,
    });

    if (error) {
      await setRetryFlag(true);
      return;
    }

    // The RPC returns the device_session uuid. Persist it for self-identify / self-revoke / wipe clear.
    const sessionId = typeof data === 'string' ? data : (data as { id?: string } | null)?.id;
    if (sessionId) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({ key: DEVICE_SESSION_ID_KEY, value: sessionId });
      } catch {
        /* persistence is best-effort — the row exists server-side regardless */
      }
    }

    await setRetryFlag(false);
  } catch {
    // Offline / transient: leave the retry flag so the next login/reconnect re-runs. Never throw.
    await setRetryFlag(true).catch(() => undefined);
  } finally {
    inFlight = false;
  }
}

/** Read the locally persisted device_session id (null on web / not-yet-registered). */
export async function getStoredDeviceSessionId(): Promise<string | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: DEVICE_SESSION_ID_KEY });
    return value ?? null;
  } catch {
    return null;
  }
}

/**
 * Clear the locally persisted device_session id (and the retry flag). Used by the wipe handler so a wiped
 * device leaves no stale session id behind. Best-effort, never throws.
 */
export async function clearDeviceSession(): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: DEVICE_SESSION_ID_KEY });
    await Preferences.remove({ key: DEVICE_SESSION_RETRY_KEY });
  } catch {
    /* preferences unavailable — ignore */
  }
}
